import Highcharts from 'highcharts'
import { resolutionLevel, resolutions, intervals } from './chartConstants'

export const handleTimezoneOffset = (currentDate) => {
  //to be able to convert from UTC
  if (currentDate.getTimezoneOffset() > 0) {
    return currentDate.getMinutes() - Math.abs(currentDate.getTimezoneOffset())
  } else if (currentDate.getTimezoneOffset() < 0) {
    return currentDate.getMinutes() + Math.abs(currentDate.getTimezoneOffset())
  } else {
    return currentDate.getMinutes()
  }
}

export const acceptedIntervalResolution = (startDateAsMillisec, endDateAsMillisec, availablePeriods) => {
  //checks if interval is between another interval, if so returns resolution. TODO: check for timezonediff..
  const acceptedInterval = availablePeriods.filter((item) => {
    return startDateAsMillisec >= new Date(item.periodStartDate).getTime() && endDateAsMillisec <= new Date(item.periodEndDate).getTime()
  })
  return acceptedInterval.length === 1 ? acceptedInterval[0].resolution : ''
}

export const isDrillDownAvailable = (startDateAsMillisec, endDateAsMillisec, availablePeriods, resolution) => {
  const acceptedResolution = acceptedIntervalResolution(startDateAsMillisec, endDateAsMillisec, availablePeriods)
  return acceptedResolution !== '' && resolutionLevel(acceptedResolution) !== -1 && resolutionLevel(resolution) !== -1 && resolutionLevel(acceptedResolution) >= resolutionLevel(resolution)
}

export const createConsumptionDataItem = (itemArray, currentResolution, isCompareItem = false, typeOfEnergy, partialPrice, featureSettingsPowerTariff) => {
  const currentResolutionAsNumber = currentResolution === 'Monthly' ? 1 : currentResolution === 'Weekly' ? 2 : currentResolution === 'Daily' ? 3 : 4
  const consumptionArray = itemArray.map((item) => {
    //INFO: To make consumption, effect and temperature match on the x axis, only yyyy-mm-dd is used when the resolution is above hourly
    let parsedDate = currentResolution === resolutions.HOURLY || currentResolution === resolutions.QUARTERLY ? new Date(item.start) : new Date(new Date(item.start).toBackendDate())
    return {
      x: parsedDate.getTime() + (isCompareItem ? 1 : 0),
      y: typeOfEnergy === 'Reaktiv' ? item.reactive : item.total,
      drilldown: item.canFilter && !featureSettingsPowerTariff,
      start: item.start,
      end: item.end,
      resolution: currentResolutionAsNumber,
      statusMeasured: item.status,
      statusAdditional: item.status === 'Additional', //Testdata - we do not have mocked item.status for month. (Add index to map function above.) => //index > 27 && !isCompareItem ? true : false,
      isConsumption: true,
      unit: typeOfEnergy === 'Reaktiv' ? 'kVArh' : 'kWh',
      date: parsedDate,
      tooltipText: item.tooltipText, //'Under denna period hade du månadsmätt mätare. Prova att byta tidsintervall.',
      partialPrice: partialPrice,
      quarters: item.quarters,
      maxPower: item.maxPower,
      debMaxPower: item.debMaxPower

      //isCompareItem
    }
  })

  return consumptionArray
}

export const createTemperatureDataItem = (itemArray, currentResolution) => {
  const temperatureArray = itemArray.map((item) => {
    //INFO: To make consumption, effect and temperature match on the x axis, only yyyy-mm-dd is used when the resolution is above hourly
    let parsedDate = currentResolution === resolutions.HOURLY || currentResolution === resolutions.QUARTERLY ? new Date(item.timeStamp) : new Date(new Date(item.timeStamp).toBackendDate())

    return {
      x: parsedDate.getTime(),
      y: item.temperature,
      resolution: currentResolution,
      isTemperature: true,
      date: parsedDate
    }
  })
  return temperatureArray
}

export const createEffectDataItem = (itemArray, currentResolution, featureSettingsForecast) => {
  const effectArray = itemArray.map((item) => {
    //INFO: To make consumption, effect and temperature match on the x axis, only yyyy-mm-dd is used when the resolution is above hourly
    let parsedDate = currentResolution === resolutions.HOURLY || currentResolution === resolutions.QUARTERLY ? new Date(item.start) : new Date(new Date(item.start).toBackendDate())

    return {
      x: parsedDate.getTime(),
      y: item.maxPower.status && item.maxPower.status === 'Missing' ? null : item.maxPower.value,
      resolution: currentResolution,
      isEffect: true,
      date: parsedDate,
      start: item.start,
      end: item.end,
      drilldown: item.canFilter && !featureSettingsForecast,
      timeString: item.maxPower.timeString,
      tooltipText: item.tooltipText
    }
  })

  return effectArray
}

export const createDebEffectDataItem = (itemArray, currentResolution, featureSettingsForecast) => {
  const effectArray = itemArray.map((item) => {
    //INFO: To make consumption, effect and temperature match on the x axis, only yyyy-mm-dd is used when the resolution is above hourly
    let parsedDate = currentResolution === resolutions.HOURLY || currentResolution === resolutions.QUARTERLY ? new Date(item.start) : new Date(new Date(item.start).toBackendDate())

    return {
      x: parsedDate.getTime(),
      y: item.debMaxPower?.status && item.debMaxPower?.status === 'Missing' ? null : item.debMaxPower?.value,
      resolution: currentResolution,
      isEffect: true,
      date: parsedDate,
      start: item.start,
      end: item.end,
      drilldown: item.canFilter && !featureSettingsForecast,
      timeString: item.debMaxPower.timeString,
      tooltipText: item.tooltipText
    }
  })

  return effectArray
}

export const currentEnergyForPeriod = (responseData, currentId, tabValue) => {
  const isSummarizadData = currentId.includes('all') || currentId.includes('group')
  const energyForPeriodAsString = setPresentationOfEnergyForPeriod(tabValue === 'Reaktiv' ? responseData.reactiveEnergyForPeriod : responseData.energyForPeriod, responseData.responseStartPeriod, responseData.responseEndPeriod, currentId)
  return isSummarizadData && energyForPeriodAsString.indexOf(',') !== -1 ? energyForPeriodAsString.substring(0, energyForPeriodAsString.indexOf(',')) : energyForPeriodAsString
}

const IntervalLargerThanAMonth = (startPeriod, endPeriod) => {
  const startDate = new Date(startPeriod)
  const endDate = new Date(endPeriod)

  return endDate.getTime() - startDate.getTime() > 1000 * 60 * 60 * 24 * 32
}

const setPresentationOfEnergyForPeriod = (energyForPeriod, start, end, currentId) => {
  const isSummarizadData = currentId.includes('all') || currentId.includes('group')
  return parseFloat(energyForPeriod)
    .toDec(isSummarizadData || IntervalLargerThanAMonth(start, end) ? 0 : 2)
    .addThousandSeperatorDefaultZero()
}

/**
 *
 * @param {*} resultData Energy data response
 * @param {*} currentInterval Current interval object
 * @param {*} currentResolution Current resolution object
 * @param {*} featureSettings Feature settings object
 * @returns
 */

export const generateEffectDataPoints = (resultData, currentResolution, monthlyPowerInformation) => {
  const formatDateString = (date, resolution) => {
    const dateObj = new Date(date)
    if (resolution !== resolutions.MONTHLY && resolution !== resolutions.WEEKLY) {
      return Highcharts.dateFormat('%e %B', dateObj.getTime())
    }
    return Highcharts.dateFormat('%e %B %Y', dateObj.getTime())
  }

  const findMatchingTopPower = (date) => {
    return monthlyPowerInformation?.topPowerValues.find((topPower) => topPower.date === date)
  }

  const effectData = resultData
    .map((item, index) => {
      const matchingTopPower = findMatchingTopPower(item.maxPower?.date)
      if (matchingTopPower) {
        return {
          ...item,
          dateString: formatDateString(matchingTopPower.date, currentResolution.value),
          timeString: matchingTopPower.timeString,
          index
        }
      }
      return null
    })
    .filter((item) => item && item.maxPower?.status !== 'Missing')

  effectData.sort((a, b) => (a.maxPower?.value < b.maxPower?.value ? 1 : a.maxPower?.value > b.maxPower?.value ? -1 : 0))

  if (effectData.length > 0) {
    return {
      averagePowerValue: currentResolution.value === intervals.DAILY ? monthlyPowerInformation?.calculatedAvgMaxPower : null,
      averagePowerLabel: currentResolution.value === intervals.DAILY ? monthlyPowerInformation?.calculatedAvgMaxPower.toFixed(2).toString().replace('.', ',') + ' kW' : null,
      items: effectData
    }
  }

  return null
}

// Deb Effect
export const generateDebEffectDataPoints = (resultData, currentResolution, monthlyPowerInformation) => {
  const formatDateString = (date, resolution) => {
    const dateObj = new Date(date)
    if (resolution !== resolutions.MONTHLY && resolution !== resolutions.WEEKLY) {
      return Highcharts.dateFormat('%e %B', dateObj.getTime())
    }
    return Highcharts.dateFormat('%e %B %Y', dateObj.getTime())
  }

  const findMatchingTopDebPower = (date) => {
    return monthlyPowerInformation?.topDebPowerValues.find((topDebPower) => topDebPower.date === date)
  }

  const effectData = resultData
    .map((item, index) => {
      const matchingTopDebPower = findMatchingTopDebPower(item.debMaxPower?.date)
      if (matchingTopDebPower) {
        return {
          ...item,
          dateString: formatDateString(matchingTopDebPower.date, currentResolution.value),
          timeString: matchingTopDebPower.timeString,
          index
        }
      }
      return null
    })
    .filter((item) => item && item.debMaxPower?.status !== 'Missing')

  effectData.sort((a, b) => (a.debMaxPower?.value < b.debMaxPower?.value ? 1 : a.debMaxPower?.value > b.debMaxPower?.value ? -1 : 0))

  if (effectData.length > 0) {
    return {
      averagePowerValue: currentResolution.value === intervals.DAILY ? monthlyPowerInformation?.calculatedDebAvgMaxPower : null,
      averagePowerLabel: currentResolution.value === intervals.DAILY ? monthlyPowerInformation?.calculatedDebAvgMaxPower.toFixed(2).toString().replace('.', ',') + ' kW' : null,
      items: effectData
    }
  }

  return null
}
